import { useEffect, useState } from 'react';
import { Box, Typography, Sheet, Button, Chip, IconButton, CircularProgress } from '@mui/joy';
import { BookmarkSimple, X, Play } from '@phosphor-icons/react';
import { APIGetFavouriteQuestions, APIRemoveFavourite } from '../../api';

/**
 * Вкладка «Избранные» в статистике: всё, что человек сохранил закладкой в тестах.
 * Отсюда можно убрать вопрос или собрать тест только из сохранённых.
 */
const strip = (html) => String(html || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
const NAVY = '#2A75D3';

export default function FavouritesTab({ token, onCreateTest }) {
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await APIGetFavouriteQuestions(token, 1, 200);
                if (!alive) return;
                if (res?.success) { setItems(res.favourite_questions || []); setTotal(res.pagination?.total_count ?? (res.favourite_questions || []).length); }
                else setError(res?.error || 'Не удалось загрузить');
            } catch (e) { if (alive) setError('Не удалось загрузить избранное'); }
        })();
        return () => { alive = false; };
    }, [token]);

    const remove = async (qid) => {
        try {
            await APIRemoveFavourite(token, qid);
            setItems((cur) => cur.filter((f) => f.question?.id !== qid));
            setTotal((n) => Math.max(0, n - 1));
        } catch (e) { setError('Не удалось убрать из избранного'); }
    };

    if (items === null && !error) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size="sm" /></Box>;

    return (
        <Sheet variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', borderColor: 'divider', bgcolor: 'background.surface' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.015em' }}>Избранные вопросы</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Сохранено: {total}</Typography>
                </Box>
                {items && items.length > 0 && (
                    <Button startDecorator={<Play size={16} weight="fill" />} onClick={onCreateTest}
                        sx={{ bgcolor: '#37352F', color: '#fff', fontWeight: 700, borderRadius: '10px', '&:hover': { bgcolor: '#1F1F1F' } }}>
                        Собрать тест из избранных
                    </Button>
                )}
            </Box>
            {error && <Typography level="body-sm" sx={{ color: '#B84C31', mb: 1.5 }}>{error}</Typography>}
            {items && items.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <BookmarkSimple size={36} color="#9B9A97" />
                    <Typography sx={{ fontWeight: 600, mt: 1 }}>Пока ничего не сохранено</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Во время теста нажмите закладку у вопроса, и он появится здесь. Потом из сохранённых можно собрать отдельный тест.
                    </Typography>
                </Box>
            )}
            {items && items.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {items.map((f, i) => {
                        const q = f.question || {};
                        const cats = (q.categories || []).slice(0, 2);
                        return (
                            <Box key={f.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 1.5, borderTop: i ? '1px solid' : 'none', borderColor: 'divider' }}>
                                <Typography sx={{ width: 28, flexShrink: 0, fontSize: 12, fontWeight: 700, color: 'text.tertiary', pt: '3px' }}>{i + 1}</Typography>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 14, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {strip(q.text)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                                        {q.clinical_case && <Chip size="sm" variant="soft" sx={{ bgcolor: `${NAVY}14`, color: NAVY, fontWeight: 600 }}>клин. задача</Chip>}
                                        {cats.map((c) => <Chip key={c.id} size="sm" variant="outlined" color="neutral">{c.name}</Chip>)}
                                    </Box>
                                </Box>
                                <IconButton size="sm" variant="plain" color="neutral" title="Убрать из избранного" onClick={() => remove(q.id)}>
                                    <X size={16} />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Sheet>
    );
}
